import {
  type BriqpayCartItem,
  type Currency,
  type BriqpayOrder,
  type BriqpayStandardCartItem,
  type DiscountDTO,
  type MinorUnit,
  type Option,
  type ShippingOption,
  type StorefrontOrderLine
} from 'ecosystem'
import { getProductImages } from 'shared-utils/utils/products'
import {
  type BuyerVatRates,
  createMinorUnit,
  getTax,
  getVatRateByCountryCode,
  roundNumber,
  shippingForBuyer,
  storeGrossRebaser,
  sumArrayOfNumbers
} from 'shared-utils'
import { orderLineValueCalculator } from '../../payment'

/**
 * `getProductImages` and `getNetPrice` come straight from source rather than through
 * `api/utils/products` / `api/utils/nexi`: both of those pull in `api/utils/products`,
 * which imports `storefront-api`, and this module is itself reached from
 * `storefront-api/ordersEndpoint` during provisioning — the round trip is a circular
 * import that fails at runtime with "Cannot access 'provisionOrder' before
 * initialization".
 */
const getNetPrice = (price: MinorUnit, taxRate: MinorUnit) => price - getTax(price, taxRate, true)

/**
 * Build one Briqpay cart row from already-resolved gross figures.
 *
 * Briqpay validates each row on its own, and the messages are literal:
 *
 * ```
 * 400 INVALID_DATA  Cart item (…) - taxRate * unitPrice * quantity != totalVatAmount,
 *                                   unitPriceIncVat * quantity != totalAmount.
 * ```
 *
 * The check is discount-aware — a `unitDiscountAmountIncVat` legitimately puts
 * `totalAmount` below `unitPriceIncVat * quantity` — but any *unexplained* gap is
 * rejected. So every figure here is derived from the already-rounded values above it
 * rather than from the raw basket, otherwise a sub-öre drift turns into a hard 400.
 *
 * Note the order-level `amountIncVat` / `amountExVat` are *not* validated against the
 * cart; the rounding discipline that matters is per row.
 */
const createCartRow = ({
  productType,
  name,
  reference,
  quantity,
  quantityUnit,
  taxRate,
  grossUnitPrice,
  totalDiscountAmount = 0,
  imageUrl
}: {
  productType: BriqpayStandardCartItem['productType']
  name: string
  reference?: string
  quantity: number
  quantityUnit: string
  taxRate: MinorUnit
  /** VAT-inclusive unit price in minor units, before any discount. */
  grossUnitPrice: number
  /** VAT-inclusive discount for the whole line, in minor units. */
  totalDiscountAmount?: number
  imageUrl?: string
}): BriqpayStandardCartItem => {
  const unitPriceIncVat = roundNumber(grossUnitPrice, 0)
  // Briqpay expresses discounts per unit. Prefer that over a separate `discount` cart
  // row: not every PSP behind Briqpay accepts order-line discounts, but all of them
  // accept per-item ones.
  const unitDiscountAmountIncVat = quantity ? roundNumber(totalDiscountAmount / quantity, 0) : 0
  const totalAmount = (unitPriceIncVat - unitDiscountAmountIncVat) * quantity

  return {
    productType,
    name,
    ...(reference ? { reference } : {}),
    quantity,
    quantityUnit,
    taxRate,
    // Ex-VAT and ex-discount, per Briqpay's convention.
    unitPrice: roundNumber(getNetPrice(unitPriceIncVat, taxRate), 0),
    unitPriceIncVat,
    discountPercentage: 0,
    unitDiscountAmountIncVat,
    totalAmount,
    totalVatAmount: roundNumber(getTax(totalAmount, taxRate, true), 0),
    ...(imageUrl ? { imageUrl } : {})
  }
}

const mapOrderLinesToBriqpayCartItems = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null
): BriqpayStandardCartItem[] =>
  items.map((item) => {
    const { product, quantity, vatRate } = item
    const taxRate = createMinorUnit(vatRate)
    const { unitPrice, totalDiscountAmount } = orderLineValueCalculator(item, cartDiscountCode)
    const { mainImageUrl } = getProductImages(product.medias)

    return createCartRow({
      productType: 'physical',
      name: product.name,
      reference: product.articleNumber,
      quantity,
      quantityUnit: 'pc',
      taxRate,
      grossUnitPrice: unitPrice,
      totalDiscountAmount,
      imageUrl: mainImageUrl
    })
  })

const servicesOrderLineFactory = (
  items: StorefrontOrderLine[],
  taxRate: MinorUnit,
  rebaseServicePrice: (grossMajor: number) => number
): BriqpayStandardCartItem[] =>
  items
    .map((item) =>
      item.services.map((service) => {
        const quantity = service.quantity
        let unitPrice = createMinorUnit(rebaseServicePrice(service.service.price))
        const discountFactor = createMinorUnit(rebaseServicePrice(service.service.discountPrice))
        let grossTotalAmount = 0

        /*
         Basic formula for calculating price of service:
         service.price + service.discountPrice * (quantity - 1)
         */
        if (quantity > 0) {
          grossTotalAmount = unitPrice + (quantity - 1) * discountFactor

          if (!unitPrice && discountFactor) {
            unitPrice = discountFactor
          }
        }

        // The staggered pricing above means the line total is not `unitPrice * quantity`,
        // which is exactly the identity Briqpay checks. Express the difference as the
        // line discount so the row reconciles.
        return createCartRow({
          productType: 'surcharge',
          name: service.service.name,
          reference: service.service.id,
          quantity,
          quantityUnit: 'service',
          taxRate,
          grossUnitPrice: unitPrice,
          totalDiscountAmount: roundNumber(unitPrice, 0) * quantity - grossTotalAmount
        })
      })
    )
    .flat()

/**
 * Turn the storefront basket into Briqpay cart rows.
 *
 * `StorefrontOrderLine.unitPrice` is buyer-resolved and VAT-*inclusive*; Briqpay's
 * `unitPrice` is ex-VAT and ex-discount. Getting that backwards is silent — the widget
 * renders and the totals are simply wrong — so the conversion lives in one place.
 */
export const briqpayOrderLineFactory = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null,
  countryCode?: string,
  buyerVat?: Option<BuyerVatRates>
) => {
  // Product rows carry each line's own `vatRate`, already resolved for the buyer
  // upstream. Service rows have no rate of their own, so they take the store's rate —
  // resolved here for the same buyer so the two agree.
  const domesticRate = getVatRateByCountryCode(countryCode)
  const taxRate = createMinorUnit(buyerVat?.resolveRate(domesticRate) ?? domesticRate)
  const productOrderLines = mapOrderLinesToBriqpayCartItems(items, cartDiscountCode)
  const serviceOrderLines = servicesOrderLineFactory(
    items,
    taxRate,
    storeGrossRebaser(domesticRate, buyerVat)
  )
  const orderLines = [...productOrderLines, ...serviceOrderLines]
  const orderAmount = sumArrayOfNumbers(orderLines.map((item) => item.totalAmount ?? 0))
  // The same basket net of VAT, in the units `orderAmount` uses. Feeds `netOrderAmount`
  // on the shipping factories so a delivery rate configured ex-VAT is evaluated against
  // the figure it was written on.
  const netOrderAmount = sumArrayOfNumbers(
    orderLines.map((item) => (item.totalAmount ?? 0) - (item.totalVatAmount ?? 0))
  )

  return {
    orderLines,
    orderAmount,
    netOrderAmount,
    taxRate,
    /** Move a carrier's VAT-inclusive quote onto this buyer's rate. Unit-agnostic
     *  (a pure ratio), so minor units pass through unchanged. */
    rebaseShipping: (grossQuote: number) =>
      shippingForBuyer(grossQuote, domesticRate, buyerVat).cost
  }
}

/**
 * Freight as a Briqpay cart row. Briqpay has no native shipping-method list — its
 * `shipping` module collects an *address* — so every carrier branch pushes the chosen
 * rate in as a `shipping_fee` row.
 */
export const briqpayShippingLine = (shippingInfo: ShippingOption): BriqpayStandardCartItem => ({
  productType: 'shipping_fee',
  name: shippingInfo.name,
  reference: shippingInfo.id,
  quantity: 1,
  quantityUnit: 'pc',
  taxRate: shippingInfo.tax_rate,
  unitPrice: shippingInfo.price - shippingInfo.tax_amount,
  unitPriceIncVat: shippingInfo.price,
  discountPercentage: 0,
  unitDiscountAmountIncVat: 0,
  totalAmount: shippingInfo.price,
  totalVatAmount: shippingInfo.tax_amount
})

/**
 * Freight from a third-party carrier as a Briqpay cart row.
 *
 * `grossIncVat` must already be in **minor units**, and the carriers disagree about what
 * they hand you: PostNord and Nshift quote in major units (139 kr as `139`), Ingrid in
 * minor (`13900`). Convert at the call site with `createMinorUnit` where needed — getting
 * it wrong is silent, and shows up as freight priced 100x too low at the payment step.
 */
export const briqpayCarrierShippingLine = ({
  name,
  reference,
  grossIncVat,
  taxRate
}: {
  name: string
  reference: string
  grossIncVat: MinorUnit
  taxRate: MinorUnit
}): BriqpayStandardCartItem => {
  const totalAmount = roundNumber(grossIncVat, 0)
  const totalVatAmount = roundNumber(getTax(totalAmount, taxRate, true), 0)

  return {
    productType: 'shipping_fee',
    name,
    reference,
    quantity: 1,
    quantityUnit: 'pc',
    taxRate,
    unitPrice: totalAmount - totalVatAmount,
    unitPriceIncVat: totalAmount,
    discountPercentage: 0,
    unitDiscountAmountIncVat: 0,
    totalAmount,
    totalVatAmount
  }
}

const isStandardCartItem = (item: BriqpayCartItem): item is BriqpayStandardCartItem =>
  item.productType !== 'sales_tax' && item.productType !== 'gift_card'

/**
 * Wrap cart rows in `data.order`. Both totals are summed from the rounded rows so they
 * agree with what Briqpay renders, even though only the rows themselves are validated.
 */
export const briqpayOrderFactory = (cart: BriqpayCartItem[], currency: Currency): BriqpayOrder => {
  const standardRows = cart.filter(isStandardCartItem)
  const amountIncVat = sumArrayOfNumbers(standardRows.map((item) => item.totalAmount ?? 0))
  const totalVat = sumArrayOfNumbers(standardRows.map((item) => item.totalVatAmount ?? 0))

  return {
    currency,
    amountIncVat,
    amountExVat: amountIncVat - totalVat,
    cart
  }
}
